/**
 * <HorizontalList> — a horizontally-scrolling row of items (atom, L0.1).
 *
 * A thin, token-styled wrapper over a horizontal `<ScrollView>`: `gap` spaces the children and
 * `padding` insets the content (enough to keep corner overlays — e.g. a `<MediaThumbnail>` trash
 * badge — from being clipped by the scroll edge). Generic, `children`-based — the "image track" of
 * `<ImageAttachment>`, but reusable for any horizontal strip. On the web, a mouse scrolls it too —
 * wheel and click-drag (`useMouseScroll`) — since the hidden scroll bar leaves it no other way.
 */
import React, { useEffect, useRef } from "react"
import { ScrollView, type ScrollViewProps } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useMouseScroll } from "../../hooks/useMouseScroll"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { dsScrollProps } from "../../utils/webScrollbars"

export type HorizontalListProps = {
    children: React.ReactNode
    /** Space between items — spacing token, default `"3"`. */
    gap?: SpacingKey
    /** Inset around the row (all sides) — spacing token, default `"0"`. */
    padding?: SpacingKey
    /** Show the native scroll indicator. Defaults to false. */
    showsScrollIndicator?: boolean
    /**
     * Centre la rangée tant qu'elle tient dans la largeur — au-delà, elle défile comme d'habitude.
     * Défaut `false` : la rangée commence à gauche, comme aujourd'hui.
     */
    center?: boolean
    /** Transmis au `ScrollView` — `"handled"` quand la rangée sert sous un champ dont le clavier est levé. */
    keyboardShouldPersistTaps?: ScrollViewProps["keyboardShouldPersistTaps"]
    /** Amène la rangée à cette position, quand elle change — l'onglet ouvert, à l'écran. Absent : rien. */
    scrollToX?: number
    testID?: string
}

export function HorizontalList(props: HorizontalListProps) {
    const {
        children,
        gap = "3",
        padding = "0",
        showsScrollIndicator = false,
        center = false,
        keyboardShouldPersistTaps,
        scrollToX,
        testID,
    } = props
    const scroller = useRef<ScrollView>(null)
    useMouseScroll(scroller)
    useEffect(() => {
        if (scrollToX != null) scroller.current?.scrollTo({ x: scrollToX, animated: true })
    }, [scrollToX])

    return (
        <ScrollView
            ref={scroller}
            {...dsScrollProps()}
            horizontal
            showsHorizontalScrollIndicator={showsScrollIndicator}
            contentContainerStyle={styles.content(gap, padding, center)}
            keyboardShouldPersistTaps={keyboardShouldPersistTaps}
            testID={testID}
        >
            {children}
        </ScrollView>
    )
}

const styles = StyleSheet.create((theme) => ({
    // `flexGrow` donne au contenu toute la largeur disponible : sans lui `justifyContent` n'a rien
    // à distribuer et le centrage ne se voit pas.
    content: (gap: SpacingKey, padding: SpacingKey, center: boolean) => ({
        gap: theme.spacing[gap],
        padding: theme.spacing[padding],
        flexGrow: center ? 1 : 0,
        justifyContent: center ? ("center" as const) : ("flex-start" as const),
    }),
}))
